// Typed API client for the yahoogle backend.
// Contracts mirror the Y-API chunk handler JSON shapes (P14 read-API).
// All fetch calls use apiFetch (credentials:'include', non-2xx → ApiError).

import { apiFetch } from "@avagency/frontend/lib/api";

// RFC 4122 UUID shape — same pattern as the backend uuidRE guard.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function assertUUID(value: string, label: string): void {
  if (!UUID_RE.test(value)) {
    throw new Error(`${label} is not a valid UUID: ${value}`);
  }
}

// ──────────────────────── Integrations ────────────────────────

export interface Integration {
  id: string;
  provider: string; // "mailchimp" | "brevo"
  display_name: string;
  status: string; // "active" | "revoked" | "error" | "reauth_required"
}

export async function fetchIntegrations(): Promise<Integration[]> {
  return apiFetch<Integration[]>("/api/v1/integrations");
}

// ──────────────────────── Domains ────────────────────────

export interface Domain {
  id: string;
  integration_id: string;
  fqdn: string;
  last_score: number | null;
  last_grade: string; // "A" | "B" | "C" | "D" | "F" | ""
  last_scanned_at: string | null; // RFC 3339
}

export async function fetchDomains(integrationId?: string): Promise<Domain[]> {
  const params = new URLSearchParams();
  if (integrationId) {
    assertUUID(integrationId, "integrationId");
    params.set("integration_id", integrationId);
  }
  const qs = params.size ? `?${params.toString()}` : "";
  return apiFetch<Domain[]>(`/api/v1/domains${qs}`);
}

// ──────────────────────── Scans ────────────────────────

export interface Scan {
  id: string;
  domain_id: string;
  integration_id: string;
  started_at: string; // RFC 3339
  finished_at: string | null;
  score: number | null;
  grade: string; // "A" | "B" | "C" | "D" | "F" | ""
  status: string; // "pending" | "running" | "succeeded" | "failed"
  failure_reason?: string;
}

export async function fetchScans(domainId: string): Promise<Scan[]> {
  assertUUID(domainId, "domainId");
  const params = new URLSearchParams({ domain_id: domainId });
  return apiFetch<Scan[]>(`/api/v1/scans?${params.toString()}`);
}

// ──────────────────────── Dashboard ────────────────────────

export interface DashboardData {
  total_domains: number;
  scans_last_30d: number;
  grade_distribution: Record<string, number>; // { "A": 3, "B": 5, ... }
  total_complaints: number;
}

export async function fetchDashboard(): Promise<DashboardData> {
  return apiFetch<DashboardData>("/api/v1/dashboard");
}
